import { ApolloError, useLazyQuery } from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { useState } from 'react';
import { gql } from '@/__generated__';

const SUMMARIZE_STREAM_POST = gql(`
  query SummarizeStreamPost($postId: ID!) {
    summarizeStreamPost(postId: $postId)
  }
`);

export const MIN_SUMMARIZABLE_POST_CHARS = 300;

const isRateLimitError = (error: ApolloError) =>
  error.graphQLErrors.some((graphQLError) => {
    const originalError = graphQLError.extensions?.originalError as
      | { statusCode?: number }
      | undefined;
    return (
      originalError?.statusCode === 429 ||
      graphQLError.message.includes('Rate limit exceeded')
    );
  });

export const useSummarizePost = (postId: string) => {
  const toast = useToast();
  const [isSummaryVisible, setIsSummaryVisible] = useState(false);

  const [summarize, { data, loading }] = useLazyQuery(SUMMARIZE_STREAM_POST, {
    variables: { postId },
    onError: (error) => {
      setIsSummaryVisible(false);
      if (isRateLimitError(error)) {
        toast.error({
          title: 'Summarization limit reached',
          subTitle:
            'You can summarize up to 50 posts per hour. Please try again later.',
        });
      } else {
        toast.error({
          title: 'Failed to summarize post',
          subTitle: 'Please try again',
        });
      }
    },
  });

  const toggleSummary = () => {
    if (isSummaryVisible) {
      setIsSummaryVisible(false);
      return;
    }
    setIsSummaryVisible(true);
    void summarize();
  };

  return {
    toggleSummary,
    isSummaryVisible,
    summary: data?.summarizeStreamPost,
    isSummaryLoading: loading,
  };
};
